<template>
  <div>
    <section class="relative md:py-24 py-16 bg-gray-50 dark:bg-slate-800 overflow-hidden" id="about">

      <!-- Decorative background blobs -->
      <div class="absolute -top-24 -right-24 w-96 h-96 bg-orange-100 dark:bg-orange-900/10 rounded-full blur-3xl opacity-50 pointer-events-none"></div>
      <div class="absolute -bottom-24 -left-24 w-80 h-80 bg-amber-100 dark:bg-amber-900/10 rounded-full blur-3xl opacity-40 pointer-events-none"></div>

      <div class="container mx-auto relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-12 md:grid-cols-2 gap-10 items-center">

          <!-- Image Column -->
          <div class="lg:col-span-5 reveal-left">
            <div class="relative group">
              <!-- Decorative frame -->
              <div class="absolute -inset-3 rounded-2xl bg-gradient-to-br from-orange-400 to-amber-400 opacity-0 group-hover:opacity-20 transition-opacity duration-700 blur-md"></div>
              <div class="absolute -bottom-4 -right-4 w-full h-full border-2 border-orange-400/40 rounded-2xl pointer-events-none"></div>

              <img :src="hero_image"
                   class="rounded-2xl shadow-2xl relative z-10 w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                   alt="Sultan Flowers Extract Palace">

              <!-- Play button overlay -->
              <div class="absolute inset-0 flex items-center justify-center z-20">
                <a href="javascript:void(0);" @click="openVideoPopup"
                   class="play-btn w-20 h-20 rounded-full shadow-xl shadow-black/30 inline-flex items-center justify-center bg-white 
                   dark:bg-slate-900 text-orange-600 hover:scale-110 transition-all duration-300">
                  <i class="mdi mdi-play text-3xl ms-1"></i>
                </a>
              </div>

              <!-- Floating badge -->
              <div class="absolute -left-6 bottom-12 bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-4 z-30 animate-float">
                <div class="flex items-center gap-3">
                  <div>
                    <p class="text-xs text-slate-400 dark:text-slate-500">100% Pure</p>
                    <p class="text-sm font-bold text-slate-800 dark:text-white">Natural Extracts</p>
                  </div>
                </div>
              </div>

              <!-- Generation badge -->
              <div class="absolute -right-5 top-10 bg-orange-600 rounded-2xl shadow-lg shadow-orange-600/30 p-4 z-30 animate-float-delayed text-white">
                <div class="text-center">
                  <p class="text-2xl font-black leading-none">3+</p>
                  <p class="text-xs font-medium opacity-90">Generations</p>
                </div>
              </div>
            </div>
          </div><!-- end col -->

          <!-- Content Column -->
          <div class="lg:col-span-7 reveal-right">
            <div class="lg:ms-7">
              <div class="inline-flex items-center gap-2 mb-4">
                <div class="h-px w-8 bg-orange-600"></div>
                <h6 class="text-orange-600 text-sm font-semibold uppercase tracking-widest">Who We Are ?</h6>
              </div>

              <h3 class="mb-5 md:text-4xl text-3xl font-bold dark:text-white leading-tight">
                Our Palace <span class="text-orange-600">Story</span>
              </h3>

              <p class="text-slate-500 dark:text-slate-300 text-base leading-relaxed mb-6">
                Start your journey with
                <span class="text-orange-600 font-semibold">Sultan Flowers Extract Palace.</span>
                We are a Bedouin family from Egypt, known for working with natural flowers and plants.
                For generations, we have learned how to extract pure scents using traditional, natural methods. Every
                product we make comes from our land and our culture, giving you the true aroma of Egyptian extract —
                pure, natural, and made with care.
              </p>

              <!-- Stats row -->
              <div class="grid grid-cols-3 gap-4 mb-8">
                <div v-for="stat in stats" :key="stat.label"
                     class="stat-card text-center p-4 rounded-2xl bg-white dark:bg-slate-900 shadow-md hover:shadow-orange-100 dark:hover:shadow-orange-900/20 hover:-translate-y-1 transition-all duration-300">
                  <p class="text-3xl font-black text-orange-600 mb-1">{{ stat.value }}</p>
                  <p class="text-xs text-slate-500 dark:text-slate-400 font-medium">{{ stat.label }}</p>
                </div>
              </div>

              <a href="javascript:void(0);" @click="openVideoPopup"
                 class="inline-flex items-center gap-3 text-orange-600 font-semibold hover:gap-5 transition-all duration-300 group">
                <span class="w-10 h-10 rounded-full bg-orange-600 text-white flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i class="mdi mdi-play ms-0.5"></i>
                </span>
                Watch Our Story
              </a>
            </div>
          </div><!-- end col -->

        </div><!-- end grid -->
      </div><!-- end container -->
    </section>

    <!-- Video Popup -->
    <Transition name="video-popup">
      <div v-if="videoPopupVisible" class="video-popup" @click.self="closeVideoPopup">
        <div class="popup-content">
          <button class="close-button" @click="closeVideoPopup">
            <span class="sr-only">Close</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <iframe :src="videoUrl" frameborder="0" allowfullscreen></iframe>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import hero_image from "@/assets/images/hero/hero.jpg"

const videoPopupVisible = ref(false);
const videoUrl = "https://www.youtube.com/embed/";

const stats = ref([
  { value: '3+', label: 'Generations' },
  { value: '100%', label: 'Natural' },
  { value: '50+', label: 'Extracts' },
]);

const openVideoPopup = () => {
  videoPopupVisible.value = true;
  document.body.style.overflow = 'hidden';
};

const closeVideoPopup = () => {
  videoPopupVisible.value = false;
  document.body.style.overflow = '';
};

// Intersection Observer for scroll reveal
let observer;
onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('revealed');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15 });

  document.querySelectorAll('.reveal-left, .reveal-right, .stat-card').forEach(el => {
    observer.observe(el);
  });
});

onUnmounted(() => {
  if (observer) observer.disconnect();
});
</script>

<style scoped>
/* Scroll reveal animations */
.reveal-left {
  opacity: 0;
  transform: translateX(-50px);
  transition: opacity 0.8s ease, transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.reveal-right {
  opacity: 0;
  transform: translateX(50px);
  transition: opacity 0.8s ease, transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.reveal-left.revealed,
.reveal-right.revealed {
  opacity: 1;
  transform: translateX(0);
}

/* Stat cards stagger */
.stat-card {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease, transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease;
}
.stat-card.revealed {
  opacity: 1;
  transform: translateY(0);
}
.stat-card:nth-child(1) { transition-delay: 0.1s; }
.stat-card:nth-child(2) { transition-delay: 0.2s; }
.stat-card:nth-child(3) { transition-delay: 0.3s; }

/* Play button */
.play-btn {
  background: radial-gradient(circle, #fff 60%, rgba(255,255,255,0.8) 100%);
  box-shadow: 0 0 0 12px rgba(255,255,255,0.2), 0 8px 24px rgba(0,0,0,0.25);
}
.play-btn:hover {
  box-shadow: 0 0 0 20px rgba(234,88,12,0.15), 0 8px 32px rgba(0,0,0,0.3);
}

/* Float animations for badges */
@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(-1deg); }
  50% { transform: translateY(-10px) rotate(1deg); }
}
@keyframes floatDelayed {
  0%, 100% { transform: translateY(0px) rotate(1deg); }
  50% { transform: translateY(-12px) rotate(-1deg); }
}
.animate-float { animation: float 4s ease-in-out infinite; }
.animate-float-delayed { animation: floatDelayed 4s ease-in-out 1s infinite; }

/* Video Popup */
.video-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.popup-content {
  position: relative;
  max-width: 860px;
  width: 92%;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0,0,0,0.5);
}
.close-button {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
}
.close-button:hover { background: rgba(255,255,255,0.3); }

iframe {
  width: 100%;
  height: 480px;
  display: block;
}

/* Popup transition */
.video-popup-enter-active { animation: popupIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
.video-popup-leave-active { animation: popupOut 0.25s ease forwards; }
@keyframes popupIn {
  from { opacity: 0; transform: scale(0.8); }
  to   { opacity: 1; transform: scale(1); }
}
@keyframes popupOut {
  from { opacity: 1; transform: scale(1); }
  to   { opacity: 0; transform: scale(0.9); }
}
</style>
